/**
 * "Shopify is missing a permission" — the one recoverable Shopify write failure.
 *
 * Detection is by `subtype === 'shopify_scope_required'`, set by chatbot-api's
 * ShopifyScopeException and originating in yepairag's core/shopify/scopes.py.
 * It used to be a substring match on the English error sentence, which meant
 * translating that sentence — or rewording it — silently removed the reconnect
 * prompt with nothing failing.
 *
 * The envelope carries what the merchant needs to fix it themselves:
 *   data.missing_scopes  which permissions to grant
 *   data.authorize_url   where to grant them (empty when one couldn't be built)
 */

export const SHOPIFY_SCOPE_REQUIRED = 'shopify_scope_required';

/**
 * 信封的定义与判定<b>已移到 <code>@lib/core/errorEnvelope</code></b>（那里说明了为什么）——
 * 它跟 Shopify 无关，是每个后端调用都会遇到的东西。这里保留再导出，老的 import 路径不必全改。
 */
import { type ErrorEnvelope } from '@lib/core/errorEnvelope';

export { isErrorEnvelope, envelopeMessage, type ErrorEnvelope } from '@lib/core/errorEnvelope';

export class ShopifyScopeError extends Error {
  readonly missingScopes: string[];
  /** Empty when the backend couldn't build one — render the dialog without a link. */
  readonly authorizeUrl: string;

  constructor(message: string, missingScopes: string[], authorizeUrl: string) {
    super(message);
    this.name = 'ShopifyScopeError';
    this.missingScopes = missingScopes;
    this.authorizeUrl = authorizeUrl;
    // Required for `instanceof` to hold when targeting ES5-era output.
    Object.setPrototypeOf(this, ShopifyScopeError.prototype);
  }
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((s): s is string => typeof s === 'string' && !!s) : [];
}

/**
 * Build a ShopifyScopeError from an error envelope, or null when the envelope is
 * some other failure. Call this from the throw site of every API wrapper that can
 * hit a Shopify write.
 */
export function scopeErrorFromEnvelope(envelope: ErrorEnvelope): ShopifyScopeError | null {
  if (envelope.subtype !== SHOPIFY_SCOPE_REQUIRED) return null;
  const payload = (envelope.data ?? {}) as Record<string, unknown>;
  return new ShopifyScopeError(
    envelope.errorMessage || 'Your Shopify connection is missing a required permission.',
    stringList(payload.missing_scopes),
    typeof payload.authorize_url === 'string' ? payload.authorize_url : ''
  );
}

/** Narrow a caught value at a `catch` site. */
export function asShopifyScopeError(err: unknown): ShopifyScopeError | null {
  return err instanceof ShopifyScopeError ? err : null;
}
